import { Controller } from "@hotwired/stimulus"

/**
 * RT Method Type Controller
 * Handles Film vs Digital radiography method selection
 * Manages visibility of method-specific variables
 * Handles data deletion when switching method types (with confirmation)
 *
 * ISO 17636-1 (Film) vs ISO 17636-2 (Digital CR/DDA)
 */
export default class extends Controller {
  static targets = [
    "selector",              // Film/CR/DDA Selection dropdown
    "filmSection",           // Film variables section container
    "crSection",            // CR variables section container
    "ddaSection",           // DDA variables section container
    "digitalSharedSection", // Digital shared variables section (CR+DDA)
    "filmField",            // Individual film-specific fields
    "crField",              // Individual CR-specific fields
    "ddaField",             // Individual DDA-specific fields
    "digitalSharedField",   // Individual digital shared fields
    "xrayTubeSection",      // X-ray tube section container (Film + CR only, NOT DDA)
    "xrayTubeField",        // X-ray tube fields (Tube Voltage/Current) - Film + CR only, NOT DDA
    "idCoversSection"       // ID Cards & Film Covers section (Film only - ISO 17636-1)
  ]

  static outlets = ["autosave"]

  connect() {
    console.log('[rt-method-type] Controller connected')
    console.log('[rt-method-type] Has selector target:', this.hasSelectorTarget)
    console.log('[rt-method-type] Has film section target:', this.hasFilmSectionTarget)
    console.log('[rt-method-type] Has CR section target:', this.hasCrSectionTarget)
    console.log('[rt-method-type] Has DDA section target:', this.hasDdaSectionTarget)
    console.log('[rt-method-type] Has digital shared section target:', this.hasDigitalSharedSectionTarget)
    console.log('[rt-method-type] Film field targets:', this.filmFieldTargets.length)
    console.log('[rt-method-type] CR field targets:', this.crFieldTargets.length)
    console.log('[rt-method-type] DDA field targets:', this.ddaFieldTargets.length)
    console.log('[rt-method-type] Digital shared field targets:', this.digitalSharedFieldTargets.length)

    // Store current selection and pending selection for confirmation modal
    this.currentMethodType = this.getMethodType()
    this.pendingMethodType = null
    this.pendingChangeEvent = null

    console.log('[rt-method-type] Initial method type:', this.currentMethodType)

    // Initialize field visibility on page load
    this.updateFieldVisibility(this.currentMethodType, false)

    // For Tom Select dropdowns, we need to listen to their custom change event
    // Tom Select might not be initialized yet, so we need to wait
    if (this.hasSelectorTarget) {
      this.initializeTomSelectListener()
    }
  }

  /**
   * Initialize Tom Select listener (with retry logic)
   */
  initializeTomSelectListener(retryCount = 0) {
    const maxRetries = 5
    const retryDelay = 200 // ms

    const tomSelect = this.selectorTarget.tomselect

    if (tomSelect) {
      console.log('[rt-method-type] Tom Select detected, binding change event')
      tomSelect.on('change', (value) => {
        console.log('[rt-method-type] Tom Select change event:', value)
        // Manually trigger the Stimulus action
        const event = new Event('change', { bubbles: true })
        this.selectorTarget.dispatchEvent(event)
      })
    } else if (retryCount < maxRetries) {
      // Tom Select not initialized yet, retry
      console.log(`[rt-method-type] Tom Select not ready, retrying in ${retryDelay}ms (attempt ${retryCount + 1}/${maxRetries})`)
      setTimeout(() => {
        this.initializeTomSelectListener(retryCount + 1)
      }, retryDelay)
    } else {
      console.log('[rt-method-type] Tom Select not found after max retries, using standard change events only')
    }
  }

  /**
   * Handle method type change with confirmation
   */
  handleTypeChange(event) {
    event.preventDefault()

    const newMethodType = this.getMethodType()

    console.log('[rt-method-type] Method type change requested:', {
      current: this.currentMethodType,
      new: newMethodType
    })

    // If it's the same method type, just update visibility (no confirmation needed)
    if (newMethodType === this.currentMethodType) {
      console.log('[rt-method-type] Same method type selected, no change needed')
      return
    }

    // If no data exists yet (initial setup), allow switch without confirmation
    if (!this.hasExistingData()) {
      console.log('[rt-method-type] No existing data, switching without confirmation')
      this.currentMethodType = newMethodType
      this.updateFieldVisibility(newMethodType, true)
      this.triggerAutosave()
      return
    }

    // Store the pending change and show confirmation modal
    this.pendingMethodType = newMethodType
    this.pendingChangeEvent = event

    // Show Bootstrap modal for confirmation
    this.showConfirmationModal()
  }

  /**
   * Show confirmation modal
   */
  showConfirmationModal() {
    const modal = document.getElementById('methodTypeChangeModal')
    if (modal) {
      // Use Bootstrap 4 modal API
      if (window.jQuery) {
        window.jQuery(modal).modal('show')
      } else {
        // Fallback if jQuery is not available
        modal.classList.add('show')
        modal.style.display = 'block'

        // Add backdrop
        const backdrop = document.createElement('div')
        backdrop.className = 'modal-backdrop fade show'
        backdrop.id = 'methodTypeChangeBackdrop'
        document.body.appendChild(backdrop)
        document.body.classList.add('modal-open')
      }
    }
  }

  /**
   * Hide confirmation modal
   */
  hideConfirmationModal() {
    const modal = document.getElementById('methodTypeChangeModal')
    if (modal) {
      if (window.jQuery) {
        window.jQuery(modal).modal('hide')
      } else {
        // Fallback if jQuery is not available
        modal.classList.remove('show')
        modal.style.display = 'none'

        // Remove backdrop
        const backdrop = document.getElementById('methodTypeChangeBackdrop')
        if (backdrop) {
          backdrop.remove()
        }
        document.body.classList.remove('modal-open')
      }
    }
  }

  /**
   * Confirm method type change (called from modal)
   */
  async confirmTypeChange(event) {
    event.preventDefault()

    if (!this.pendingMethodType) {
      console.error('[rt-method-type] No pending method type change')
      return
    }

    console.log('[rt-method-type] Method type change confirmed:', this.pendingMethodType)

    // Hide modal
    this.hideConfirmationModal()

    // Delete opposite method type's data
    await this.deleteOppositeMethodData(this.pendingMethodType)

    // Update current method type
    this.currentMethodType = this.pendingMethodType
    this.pendingMethodType = null

    // Update field visibility
    this.updateFieldVisibility(this.currentMethodType, true)

    // Trigger autosave
    await this.triggerAutosave()
  }

  /**
   * Get current method type from selector (Film, CR, or DDA)
   */
  getMethodType() {
    if (!this.hasSelectorTarget) {
      console.warn('[rt-method-type] No selector target found')
      return null
    }

    const selectedValue = this.selectorTarget.value
    const selectedText = this.selectorTarget.options[this.selectorTarget.selectedIndex]?.text || ''

    console.log('[rt-method-type] Getting method type from selector:', {
      value: selectedValue,
      text: selectedText
    })

    // Normalize to lowercase for case-insensitive matching
    const valueLower = selectedValue.toLowerCase()
    const textLower = selectedText.toLowerCase()

    // Check both value and text for Film indicators (ISO 17636-1)
    if (valueLower.includes("film") ||
        valueLower.includes("17636-1") ||
        valueLower.includes("17636.1") ||
        textLower.includes("film") ||
        textLower.includes("17636-1")) {
      console.log('[rt-method-type] Detected: Film (ISO 17636-1)')
      return 'film'
    }

    // Check for CR (Computed Radiography) - must check before generic "digital"
    if (valueLower.includes("cr") || textLower.includes("cr")) {
      console.log('[rt-method-type] Detected: CR (ISO 17636-2)')
      return 'cr'
    }

    // Check for DDA (Direct Digital Acquisition) - must check before generic "digital"
    if (valueLower.includes("dda") || textLower.includes("dda")) {
      console.log('[rt-method-type] Detected: DDA (ISO 17636-2)')
      return 'dda'
    }

    // Fallback: Generic digital detection (defaults to CR)
    if (valueLower.includes("digital") ||
        valueLower.includes("17636-2") ||
        valueLower.includes("17636.2") ||
        textLower.includes("digital") ||
        textLower.includes("17636-2")) {
      console.log('[rt-method-type] Detected: Digital (defaulting to CR)')
      return 'cr'  // Default generic "digital" to CR
    }

    console.warn('[rt-method-type] Could not determine method type from:', { value: selectedValue, text: selectedText })
    return null
  }

  /**
   * Check if there's existing data in any method-specific fields
   */
  hasExistingData() {
    // Check film fields
    if (this.hasFilmFieldTarget) {
      for (const fieldContainer of this.filmFieldTargets) {
        const input = fieldContainer.querySelector('input, select')
        if (input && input.value && input.value.trim() !== '') {
          return true
        }
      }
    }

    // Check CR fields
    if (this.hasCrFieldTarget) {
      for (const fieldContainer of this.crFieldTargets) {
        const input = fieldContainer.querySelector('input, select')
        if (input && input.value && input.value.trim() !== '') {
          return true
        }
      }
    }

    // Check DDA fields
    if (this.hasDdaFieldTarget) {
      for (const fieldContainer of this.ddaFieldTargets) {
        const input = fieldContainer.querySelector('input, select')
        if (input && input.value && input.value.trim() !== '') {
          return true
        }
      }
    }

    // Check digital shared fields
    if (this.hasDigitalSharedFieldTarget) {
      for (const fieldContainer of this.digitalSharedFieldTargets) {
        const input = fieldContainer.querySelector('input, select')
        if (input && input.value && input.value.trim() !== '') {
          return true
        }
      }
    }

    return false
  }

  /**
   * Delete data from opposite method type fields
   */
  async deleteOppositeMethodData(newMethodType) {
    let fieldsToDelete = []

    // Determine which fields to delete based on new method type
    if (newMethodType === 'film') {
      // Switching to Film: delete CR, DDA, and digital shared fields
      fieldsToDelete = [
        ...(this.hasCrFieldTarget ? this.crFieldTargets : []),
        ...(this.hasDdaFieldTarget ? this.ddaFieldTargets : []),
        ...(this.hasDigitalSharedFieldTarget ? this.digitalSharedFieldTargets : [])
      ]
      console.log('[rt-method-type] Deleting CR, DDA, and digital shared data (switching to Film)')
    } else if (newMethodType === 'cr') {
      // Switching to CR: delete Film and DDA fields (keep digital shared)
      fieldsToDelete = [
        ...(this.hasFilmFieldTarget ? this.filmFieldTargets : []),
        ...(this.hasDdaFieldTarget ? this.ddaFieldTargets : [])
      ]
      console.log('[rt-method-type] Deleting Film and DDA data (switching to CR)')
    } else if (newMethodType === 'dda') {
      // Switching to DDA: delete Film and CR fields (keep digital shared)
      fieldsToDelete = [
        ...(this.hasFilmFieldTarget ? this.filmFieldTargets : []),
        ...(this.hasCrFieldTarget ? this.crFieldTargets : [])
      ]
      console.log('[rt-method-type] Deleting Film and CR data (switching to DDA)')
    }

    if (fieldsToDelete.length === 0) {
      console.log('[rt-method-type] No fields to delete')
      return
    }

    console.log(`[rt-method-type] Clearing ${fieldsToDelete.length} fields`)

    // Clear all values in opposite method type fields
    fieldsToDelete.forEach(fieldContainer => {
      const input = fieldContainer.querySelector('input')
      const select = fieldContainer.querySelector('select')

      if (input) {
        input.value = ''
        console.log('[rt-method-type] Cleared input field:', input.id)
      }

      if (select) {
        select.value = ''
        // Also update Tom Select if present
        if (select.tomselect) {
          select.tomselect.clear()
        }
        console.log('[rt-method-type] Cleared select field:', select.id)
      }
    })
  }

  /**
   * Update field visibility based on method type (Film, CR, or DDA)
   */
  updateFieldVisibility(methodType, animated = false) {
    if (!methodType) {
      console.warn('[rt-method-type] No method type specified for visibility update')
      return
    }

    const isFilm = methodType === 'film'
    const isCR = methodType === 'cr'
    const isDDA = methodType === 'dda'
    const isDigital = isCR || isDDA  // Either CR or DDA

    console.log('[rt-method-type] Updating field visibility:', {
      methodType,
      isFilm,
      isCR,
      isDDA,
      isDigital,
      hasFilmSection: this.hasFilmSectionTarget,
      hasCrSection: this.hasCrSectionTarget,
      hasDdaSection: this.hasDdaSectionTarget,
      hasDigitalSharedSection: this.hasDigitalSharedSectionTarget,
      filmFieldCount: this.filmFieldTargets?.length || 0,
      crFieldCount: this.crFieldTargets?.length || 0,
      ddaFieldCount: this.ddaFieldTargets?.length || 0,
      digitalSharedFieldCount: this.digitalSharedFieldTargets?.length || 0
    })

    // Show/hide film section
    if (this.hasFilmSectionTarget) {
      console.log('[rt-method-type] Film section:', isFilm ? 'SHOWING' : 'HIDING')
      this.toggleSection(this.filmSectionTarget, isFilm, animated)
    } else {
      console.log('[rt-method-type] No film section target found')
    }

    // Show/hide CR section
    if (this.hasCrSectionTarget) {
      console.log('[rt-method-type] CR section:', isCR ? 'SHOWING' : 'HIDING')
      this.toggleSection(this.crSectionTarget, isCR, animated)
    } else {
      console.log('[rt-method-type] No CR section target found')
    }

    // Show/hide DDA section
    if (this.hasDdaSectionTarget) {
      console.log('[rt-method-type] DDA section:', isDDA ? 'SHOWING' : 'HIDING')
      this.toggleSection(this.ddaSectionTarget, isDDA, animated)
    } else {
      console.log('[rt-method-type] No DDA section target found')
    }

    // Show/hide digital shared section (visible for both CR and DDA)
    if (this.hasDigitalSharedSectionTarget) {
      console.log('[rt-method-type] Digital shared section:', isDigital ? 'SHOWING' : 'HIDING')
      this.toggleSection(this.digitalSharedSectionTarget, isDigital, animated)
    } else {
      console.log('[rt-method-type] No digital shared section target found')
    }

    // Enable/disable film fields
    if (this.hasFilmFieldTarget) {
      this.toggleFields(this.filmFieldTargets, isFilm)
    }

    // Enable/disable CR fields
    if (this.hasCrFieldTarget) {
      this.toggleFields(this.crFieldTargets, isCR)
    }

    // Enable/disable DDA fields
    if (this.hasDdaFieldTarget) {
      this.toggleFields(this.ddaFieldTargets, isDDA)
    }

    // Enable/disable digital shared fields (for both CR and DDA)
    if (this.hasDigitalSharedFieldTarget) {
      this.toggleFields(this.digitalSharedFieldTargets, isDigital)
    }

    // Show/hide X-ray tube SECTION (Tube Voltage/Current) - visible for Film + CR only, NOT DDA
    // ISO 17636-2 DDA uses different radiation source parameters
    const showXrayTubeFields = isFilm || isCR
    if (this.hasXrayTubeSectionTarget) {
      console.log('[rt-method-type] X-ray Tube section:', showXrayTubeFields ? 'SHOWING' : 'HIDING')
      this.toggleSection(this.xrayTubeSectionTarget, showXrayTubeFields, animated)
    }
    // Also toggle individual fields for form enable/disable
    if (this.hasXrayTubeFieldTarget) {
      this.toggleFields(this.xrayTubeFieldTargets, showXrayTubeFields)
    }

    // Show/hide ID Cards & Film Covers section (Film only - ISO 17636-1)
    // This section is only relevant for physical film radiography documentation
    if (this.hasIdCoversSectionTarget) {
      console.log('[rt-method-type] ID & Covers section:', isFilm ? 'SHOWING' : 'HIDING')
      this.toggleSection(this.idCoversSectionTarget, isFilm, animated)
    }

    console.log('═══════════════════════════════════════════')
    console.log('[rt-method-type] Field visibility update COMPLETE')
    console.log('[rt-method-type] Method Type:', methodType)
    console.log('[rt-method-type] Film Section Visible:', isFilm)
    console.log('[rt-method-type] CR Section Visible:', isCR)
    console.log('[rt-method-type] DDA Section Visible:', isDDA)
    console.log('[rt-method-type] Digital Shared Section Visible:', isDigital)
    console.log('[rt-method-type] X-ray Tube Fields Visible:', showXrayTubeFields)
    console.log('[rt-method-type] ID & Covers Section Visible:', isFilm)
    console.log('[rt-method-type] Film Fields Enabled:', isFilm)
    console.log('[rt-method-type] CR Fields Enabled:', isCR)
    console.log('[rt-method-type] DDA Fields Enabled:', isDDA)
    console.log('[rt-method-type] Digital Shared Fields Enabled:', isDigital)
    console.log('[rt-method-type] X-ray Tube Fields Enabled:', showXrayTubeFields)
    console.log('═══════════════════════════════════════════')
  }

  /**
   * Helper: Toggle visibility of a section
   */
  toggleSection(section, show, animated) {
    if (show) {
      section.style.display = 'block'
      section.style.opacity = '1'
      if (animated) {
        section.classList.add('fade-in')
      }
    } else {
      section.style.display = 'none'
      section.style.opacity = '0'
    }
  }

  /**
   * Helper: Enable/disable fields in a collection
   */
  toggleFields(fieldTargets, enable) {
    fieldTargets.forEach(fieldContainer => {
      const input = fieldContainer.querySelector('input, select')
      if (input) {
        input.disabled = !enable

        // Handle Tom Select dropdowns
        if (input.tomselect) {
          if (enable) {
            input.tomselect.enable()
          } else {
            input.tomselect.disable()
          }
        }
      }
    })
  }

  /**
   * Trigger autosave via outlet
   */
  async triggerAutosave() {
    console.log('[rt-method-type] Triggering autosave...')

    if (this.hasAutosaveOutlet) {
      console.log('[rt-method-type] Autosave outlet found, calling saveNow()...')
      await this.autosaveOutlet.saveNow()
      console.log('[rt-method-type] Autosave complete')
    } else {
      console.warn('[rt-method-type] No autosave outlet available')
    }
  }

  /**
   * Cleanup when modal is cancelled
   */
  disconnect() {
    // Reset selector to current method type if there's a pending change
    if (this.pendingMethodType && this.hasSelectorTarget) {
      let currentValue

      // Map method type to expected selector value
      if (this.currentMethodType === 'film') {
        currentValue = 'Film radiography (ISO 17636-1)'
      } else if (this.currentMethodType === 'cr') {
        currentValue = 'Digital radiography - CR (ISO 17636-2)'
      } else if (this.currentMethodType === 'dda') {
        currentValue = 'Digital radiography - DDA (ISO 17636-2)'
      }

      // Find option that matches current method type
      const options = this.selectorTarget.options
      for (let i = 0; i < options.length; i++) {
        if (options[i].value.includes(currentValue) || options[i].text.includes(currentValue)) {
          this.selectorTarget.selectedIndex = i
          break
        }
      }

      // Update Tom Select if present
      if (this.selectorTarget.tomselect) {
        this.selectorTarget.tomselect.setValue(this.selectorTarget.value)
      }
    }
  }
}
